import { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../config.js';
import { differenceInDays } from 'date-fns';
import { isCompletedVisit } from '../constants/visitsClassification.js';
import { sourceParam } from '../constants/visitsSource.js';
import { countStoreVisits, physicalVisitDurations } from '../constants/visitsUnivers.js';

/**
 * Hook personnalisé pour l'analyse des objectifs de visites
 * Mutualise la logique entre VisitsTargets et VisitsPlanned
 */
const useVisitsTargetsAnalysis = ({
  visits,
  startDate,
  endDate,
  selectedProject,
  selectedShopSigns,
  fetchWithAuth
}) => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);

  // if endDate is in the future, set it to today
  const adjustedEndDate = useMemo(() => {
    return endDate > new Date() ? new Date() : endDate;
  }, [endDate]);

  const year = useMemo(() => {
    // Use the year from startDate, or current year as fallback
    return startDate ? startDate.getFullYear() : new Date().getFullYear();
  }, [startDate]);

  // Fetch targets from API
  useEffect(() => {
    const fetchTargets = async () => {
      if (!selectedProject) {
        setTargets([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/visits/${selectedProject.value}/${year}/targets?${sourceParam(selectedProject)}`
        );

        if (response.ok) {
          const targetsData = await response.json();
          setTargets(targetsData);
        } else {
          console.error("Failed to fetch targets");
          setTargets([]);
        }
      } catch (error) {
        console.error("Error fetching targets:", error);
        setTargets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTargets();
  }, [selectedProject, year, fetchWithAuth]);

  // Calculate period duration in days
  const periodDays = useMemo(() => {
    return differenceInDays(adjustedEndDate, startDate) + 1;
  }, [startDate, adjustedEndDate]);

  // Filtered targets based on selected shop signs
  const filteredTargets = useMemo(() => {
    if (!selectedShopSigns || selectedShopSigns.length === 0) return targets;
    return targets.filter(target => {
      // Check if any of the selected shop signs' labels match the intervention_point_name
      return selectedShopSigns.some(shopSign =>
        target.intervention_point_name.includes(shopSign.label)
      );
    });
  }, [targets, selectedShopSigns]);

  // Helper function to get target status for a specific store
  const getStoreTargetStatus = useMemo(() => {
    return (storeName) => {
      if (!filteredTargets.length || !visits.length) {
        return {
          status: 'no-data',
          message: 'Données insuffisantes',
          targetFrequency: null,
          targetDuration: null,
          expectedVisits: null,
          actualVisits: null,
          difference: null
        };
      }

      // Find target for this store
      const target = filteredTargets.find(t => t.intervention_point_name === storeName);

      if (!target) {
        return {
          status: 'no-target',
          message: 'Pas d\'objectif',
          targetFrequency: null,
          targetDuration: null,
          expectedVisits: null,
          actualVisits: null,
          difference: null
        };
      }

      // PHYSICAL visits, not univers-visits. The table is grained by (visit × univers)
      // since 2026-09, so a store visit covering Blanc and Brun is two rows — but
      // `visit_frequency` is a target per STORE in visits per year, and was not
      // redefined per univers. Counting rows would report 200 % attainment on a store
      // that is merely on target.
      const completedVisits = visits.filter(visit =>
        isCompletedVisit(visit) && visit.interventionPoint === storeName
      );

      const targetFrequency = target.visit_frequency;
      const targetDuration = target.visit_duration;
      const expectedVisitsInPeriod = (targetFrequency * periodDays) / 365;
      const actualVisits = countStoreVisits(completedVisits);
      const difference = actualVisits - expectedVisitsInPeriod;

      let status, message;
      if (difference < -1) {
        status = 'under-target';
        message = 'Sous-visite';
      } else if (difference > 1) {
        status = 'over-target';
        message = 'Sur-visite';
      } else {
        status = 'on-target';
        message = 'Conforme';
      }

      return {
        status,
        message,
        targetFrequency,
        targetDuration,
        expectedVisits: expectedVisitsInPeriod,
        actualVisits,
        difference,
        target
      };
    };
  }, [filteredTargets, visits, periodDays]);

  // Full analysis (for VisitsTargets component)
  const targetAnalysis = useMemo(() => {
    if (!filteredTargets.length || !visits.length) return null;

    // Create a map of completed visits by store
    const completedVisitsByStore = {};
    const visitDurationByStore = {};

    // Only consider real, held, non-deleted completed visits
    const completedVisits = visits.filter(isCompletedVisit);

    // Group visits by intervention point (store)
    completedVisits.forEach(visit => {
      if (!visit.interventionPoint) return;

      if (!completedVisitsByStore[visit.interventionPoint]) {
        completedVisitsByStore[visit.interventionPoint] = [];
      }

      completedVisitsByStore[visit.interventionPoint].push(visit);
    });

    // PHYSICAL durations here, unlike every descriptive average in the module.
    // These targets run 45 to 180 minutes and describe how long a visit should
    // last, so measuring them against one univers' allocated share would flag
    // every multi-univers store as under target. Same reasoning as the frequency
    // verdicts above: a per-store target counts physical visits.
    physicalVisitDurations(completedVisits).forEach((visit) => {
      if (!visit.interventionPoint) return;
      if (!visitDurationByStore[visit.interventionPoint]) {
        visitDurationByStore[visit.interventionPoint] = { count: 0, totalDuration: 0 };
      }
      visitDurationByStore[visit.interventionPoint].count += 1;
      visitDurationByStore[visit.interventionPoint].totalDuration += visit.durationMinutes;
    });

    // Analysis categories
    const storesWithTargetsNoVisits = [];
    const storesWithVisitsNoTargets = [];
    const storesUnderTargetFrequency = [];
    const storesConformTargetFrequency = [];
    const storesOverTargetFrequency = [];
    const storesUnderTargetDuration = [];
    const storesConformTargetDuration = [];
    const storesOverTargetDuration = [];

    // Analyze targets
    filteredTargets.forEach(target => {
      const storeName = target.intervention_point_name;
      const storeVisits = completedVisitsByStore[storeName] || [];
      const targetFrequency = target.visit_frequency;
      const targetDuration = target.visit_duration;

      const expectedVisitsInPeriod = (targetFrequency * periodDays) / 365;
      // PHYSICAL visits: `visit_frequency` is a per-store target, so counting a
      // Blanc+Brun visit twice reported 200 % attainment for a store exactly on
      // target. getStoreTargetStatus above already counts them this way.
      const actualVisits = countStoreVisits(storeVisits);
      const difference = actualVisits - expectedVisitsInPeriod;

      // Store data for analysis
      const analysisData = {
        storeName,
        sectorName: null,
        targetFrequency,
        targetDuration,
        expectedVisitsInPeriod,
        actualVisits,
        difference,
        frequencyStatus: difference < -1 ? 'Sous-visite' : (difference > 1 ? 'Sur-visite' : 'Conforme')
      };

      // Get sector name from first visit if available
      if (storeVisits.length > 0 && storeVisits[0].sectorName) {
        analysisData.sectorName = storeVisits[0].sectorName;
      }

      // Check if store has any visits
      if (storeVisits.length === 0) {
        storesWithTargetsNoVisits.push(analysisData);
      } else {
        // Compare frequency using the thresholds
        if (difference < -1) {
          storesUnderTargetFrequency.push(analysisData);
        } else if (difference > 1) {
          storesOverTargetFrequency.push(analysisData);
        } else {
          storesConformTargetFrequency.push(analysisData);
        }

        // Compare duration only if targetDuration is defined and valid
        if (targetDuration && targetDuration > 0 && visitDurationByStore[storeName]) {
          const avgDuration = Math.round(
            visitDurationByStore[storeName].totalDuration / visitDurationByStore[storeName].count
          );

          const durationDifference = avgDuration - targetDuration;
          const durationDifferencePercentage = Math.abs(durationDifference) / targetDuration * 100;

          let durationStatus;
          if (durationDifferencePercentage <= 15) {
            durationStatus = 'Conforme';
          } else if (durationDifference < 0) {
            durationStatus = 'Sous-durée';
          } else {
            durationStatus = 'Sur-durée';
          }

          const durationData = {
            ...analysisData,
            avgDuration,
            targetDuration,
            durationDifference,
            durationDifferencePercentage,
            durationStatus
          };

          // Assign to the appropriate category based on duration
          if (durationDifferencePercentage <= 15) {
            storesConformTargetDuration.push(durationData);
          } else if (durationDifference < 0) {
            storesUnderTargetDuration.push(durationData);
          } else {
            storesOverTargetDuration.push(durationData);
          }
        }
      }
    });

    // Find stores with visits but no targets
    Object.keys(completedVisitsByStore).forEach(storeName => {
      const hasTarget = filteredTargets.some(t => t.intervention_point_name === storeName);

      if (!hasTarget) {
        const visitUsers = [...new Set(completedVisitsByStore[storeName].map(visit => visit.username))];

        storesWithVisitsNoTargets.push({
          storeName,
          // Physical visits: paired with a PHYSICAL avgDuration below, and with
          // the table above that counts the same visit once.
          actualVisits: countStoreVisits(completedVisitsByStore[storeName]),
          avgDuration: visitDurationByStore[storeName] ?
            Math.round(visitDurationByStore[storeName].totalDuration / visitDurationByStore[storeName].count) : 0,
          users: visitUsers,
          primaryUser: visitUsers.length > 0 ? visitUsers[0] : '-'
        });
      }
    });

    return {
      storesWithTargetsNoVisits,
      storesWithVisitsNoTargets,
      storesUnderTargetFrequency,
      storesConformTargetFrequency,
      storesOverTargetFrequency,
      storesUnderTargetDuration,
      storesConformTargetDuration,
      storesOverTargetDuration,

      // Summary counts
      totalTargets: filteredTargets.length,
      storesWithTargetsNoVisitsCount: storesWithTargetsNoVisits.length,
      storesWithVisitsNoTargetsCount: storesWithVisitsNoTargets.length,
      storesUnderTargetFrequencyCount: storesUnderTargetFrequency.length,
      storesConformTargetFrequencyCount: storesConformTargetFrequency.length,
      storesOverTargetFrequencyCount: storesOverTargetFrequency.length,
      storesUnderTargetDurationCount: storesUnderTargetDuration.length,
      storesConformTargetDurationCount: storesConformTargetDuration.length,
      storesOverTargetDurationCount: storesOverTargetDuration.length,
    };
  }, [filteredTargets, visits, periodDays]);

  // Fonction pour calculer le statut après une visite supplémentaire
  const getStoreTargetStatusAfterVisit = useMemo(() => {
    return (storeName) => {
      if (!filteredTargets.length || !storeName) {
        return {
          status: 'no-data',
          message: 'Aucune donnée'
        };
      }

      const target = filteredTargets.find(t => t.intervention_point_name === storeName);
      if (!target) {
        return {
          status: 'no-target',
          message: 'Pas d\'objectif'
        };
      }

      // Utiliser EXACTEMENT la même logique que getStoreTargetStatus
      // PHYSICAL visits, not univers-visits. The table is grained by (visit × univers)
      // since 2026-09, so a store visit covering Blanc and Brun is two rows — but
      // `visit_frequency` is a target per STORE in visits per year, and was not
      // redefined per univers. Counting rows would report 200 % attainment on a store
      // that is merely on target.
      const completedVisits = visits.filter(visit =>
        isCompletedVisit(visit) && visit.interventionPoint === storeName
      );

      const targetFrequency = target.visit_frequency;
      const expectedVisitsInPeriod = (targetFrequency * periodDays) / 365;
      const actualVisits = countStoreVisits(completedVisits);

      // Calculer le nombre de visites APRÈS la visite planifiée (+1)
      const visitsAfterPlanned = actualVisits + 1;
      const difference = visitsAfterPlanned - expectedVisitsInPeriod;

      // Utiliser EXACTEMENT les mêmes seuils que getStoreTargetStatus
      let status, message;
      if (difference < -1) {
        status = 'under-target';
        message = 'Sous-visite';
      } else if (difference > 1) {
        status = 'over-target';
        message = 'Sur-visite';
      } else {
        status = 'on-target';
        message = 'Conforme';
      }

      return {
        status,
        message,
        actualVisits,
        visitsAfterPlanned,
        expectedVisitsInPeriod: Math.round(expectedVisitsInPeriod * 10) / 10,
        difference: Math.round(difference * 10) / 10
      };
    };
  }, [filteredTargets, visits, startDate, adjustedEndDate, selectedShopSigns, periodDays]);

  return {
    targets,
    filteredTargets,
    loading,
    targetAnalysis,
    getStoreTargetStatus,
    getStoreTargetStatusAfterVisit,
    periodDays,
    adjustedEndDate,
    year
  };
};

export default useVisitsTargetsAnalysis;
